import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { useCachedData } from '../hooks/useCachedData';
import { 
  Lightbulb, RefreshCw, DollarSign, AlertTriangle, CheckCircle2, 
  Users, UserPlus, MapPin, UserCheck, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Clock, Shield, AlertCircle, Briefcase, UsersRound, ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';

const RECOMMENDATION_ICONS = {
  REBALANCE_WORKLOAD: Users,
  HIRE_STAFF: UserPlus,
  SUPERVISOR_REVIEW: UserCheck,
  LOCATION_REVIEW: MapPin,
  FLOAT_POOL: UsersRound
};

const RECOMMENDATION_COLORS = {
  REBALANCE_WORKLOAD: 'bg-blue-500',
  HIRE_STAFF: 'bg-green-500',
  SUPERVISOR_REVIEW: 'bg-amber-500',
  LOCATION_REVIEW: 'bg-purple-500',
  FLOAT_POOL: 'bg-teal-500'
};

const PRIORITY_STYLES = {
  HIGH: 'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW: 'bg-green-100 text-green-800 border-green-200'
};

const RecommendationCard = ({ recommendation, index, onViewEmployee }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = RECOMMENDATION_ICONS[recommendation.type] || Lightbulb;
  const iconBg = RECOMMENDATION_COLORS[recommendation.type] || 'bg-gray-500';
  
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-white rounded-xl border border-border shadow-sm overflow-hidden"
      data-testid={`recommendation-card-${index}`}
    >
      {/* Card Header */}
      <div 
        className="p-5 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <Icon className="text-white" size={24} />
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className={PRIORITY_STYLES[recommendation.priority]}>
                {recommendation.priority}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {Math.round(recommendation.confidence * 100)}% confidence
              </span>
            </div>
            <h3 className="font-semibold text-[#1e3a5f] text-lg mb-1">
              {recommendation.title}
            </h3>
            <p className="text-muted-foreground text-sm">
              {recommendation.action}
            </p>
          </div>

          {/* Savings Badge */}
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(recommendation.financial_impact.annual_savings)}
            </div>
            <div className="text-xs text-muted-foreground">annual savings</div>
          </div>

          {/* Expand Icon */}
          <div className="flex-shrink-0 ml-2">
            {expanded ? (
              <ChevronUp className="text-muted-foreground" size={20} />
            ) : (
              <ChevronDown className="text-muted-foreground" size={20} />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-border"
          >
            <div className="p-5 space-y-5">
              {/* Financial Impact */}
              <div>
                <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                  <DollarSign size={18} className="text-green-600" />
                  Financial Impact
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {recommendation.financial_impact.current_annual_ot_cost && (
                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">Current OT Cost</div>
                      <div className="font-bold text-red-600">
                        {formatCurrency(recommendation.financial_impact.current_annual_ot_cost)}
                      </div>
                    </div>
                  )}
                  {recommendation.financial_impact.new_hire_cost && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">Implementation Cost</div>
                      <div className="font-bold text-blue-600">
                        {formatCurrency(recommendation.financial_impact.new_hire_cost)}
                      </div>
                    </div>
                  )}
                  <div className="bg-green-50 rounded-lg p-3">
                    <div className="text-xs text-muted-foreground mb-1">Projected Savings</div>
                    <div className="font-bold text-green-600">
                      {formatCurrency(recommendation.financial_impact.projected_savings)}
                    </div>
                  </div>
                  {recommendation.financial_impact.payback_period && (
                    <div className="bg-slate-50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">Payback Period</div>
                      <div className="font-bold text-[#1e3a5f]">
                        {recommendation.financial_impact.payback_period}
                      </div>
                    </div>
                  )}
                  {recommendation.financial_impact.roi_percentage && (
                    <div className="bg-purple-50 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1">ROI</div>
                      <div className="font-bold text-purple-600">
                        {recommendation.financial_impact.roi_percentage}%
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Details - Enhanced layout for REBALANCE_WORKLOAD recommendations */}
              {recommendation.details && Object.keys(recommendation.details).length > 0 && (
                <div>
                  <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                    <Clock size={18} className="text-blue-600" />
                    Details
                  </h4>
                  
                  {/* Special Side-by-Side layout for Rebalance Workload recommendations */}
                  {recommendation.type === 'REBALANCE_WORKLOAD' && recommendation.details.from_employee && recommendation.details.to_employee ? (
                    <div className="space-y-4">
                      {/* Employee Comparison Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* FROM Employee (Overworked) */}
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                              <TrendingUp size={16} className="text-red-600" />
                            </div>
                            <div>
                              <div className="text-xs font-medium text-red-600 uppercase tracking-wide">Shift From (Overworked)</div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {recommendation.details.from_employee_id ? (
                              <button onClick={() => onViewEmployee(recommendation.details.from_employee_id)} className="text-lg font-bold text-blue-600 hover:underline cursor-pointer transition-colors">{recommendation.details.from_employee}</button>
                            ) : (
                              <div className="text-lg font-bold text-[#1e3a5f]">{recommendation.details.from_employee}</div>
                            )}
                            <div className="flex items-center gap-4">
                              <div>
                                <div className="text-xs text-muted-foreground">Current Premium Hours</div>
                                <div className="text-2xl font-bold text-red-600">{recommendation.details.from_employee_ot?.toLocaleString()}h</div>
                              </div>
                              {recommendation.details.peer_average && (
                                <div className="text-xs text-red-500 bg-red-100 px-2 py-1 rounded-full">
                                  +{((recommendation.details.from_employee_ot - recommendation.details.peer_average) / recommendation.details.peer_average * 100).toFixed(0)}% above peer avg
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* TO Employee (Underutilized) */}
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                              <TrendingDown size={16} className="text-green-600" />
                            </div>
                            <div>
                              <div className="text-xs font-medium text-green-600 uppercase tracking-wide">Shift To (Capacity Available)</div>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {recommendation.details.to_employee_id ? (
                              <button onClick={() => onViewEmployee(recommendation.details.to_employee_id)} className="text-lg font-bold text-blue-600 hover:underline cursor-pointer transition-colors">{recommendation.details.to_employee}</button>
                            ) : (
                              <div className="text-lg font-bold text-[#1e3a5f]">{recommendation.details.to_employee}</div>
                            )}
                            <div className="flex items-center gap-4">
                              <div>
                                <div className="text-xs text-muted-foreground">Current Premium Hours</div>
                                <div className="text-2xl font-bold text-green-600">{recommendation.details.to_employee_ot?.toLocaleString()}h</div>
                              </div>
                              {recommendation.details.peer_average && (
                                <div className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                                  {((recommendation.details.to_employee_ot - recommendation.details.peer_average) / recommendation.details.peer_average * 100).toFixed(0)}% below peer avg
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hours to Shift Arrow */}
                      <div className="flex items-center justify-center">
                        <div className="bg-blue-100 border border-blue-200 rounded-full px-6 py-3 flex items-center gap-3">
                          <ArrowRight size={20} className="text-blue-600" />
                          <div className="text-center">
                            <div className="text-xs text-blue-600 font-medium">HOURS TO SHIFT</div>
                            <div className="text-xl font-bold text-blue-700">{recommendation.details.hours_to_shift}h</div>
                          </div>
                          <ArrowRight size={20} className="text-blue-600" />
                        </div>
                      </div>

                      {/* Additional Details */}
                      <div className="bg-slate-50 rounded-lg p-4">
                        <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {recommendation.details.job_title && (
                            <div>
                              <dt className="text-muted-foreground">Job Title</dt>
                              <dd className="font-medium">{recommendation.details.job_title}</dd>
                            </div>
                          )}
                          {recommendation.details.location && (
                            <div>
                              <dt className="text-muted-foreground">Location</dt>
                              <dd className="font-medium">{recommendation.details.location}</dd>
                            </div>
                          )}
                          {recommendation.details.peer_count && (
                            <div>
                              <dt className="text-muted-foreground">Peer Group Size</dt>
                              <dd className="font-medium">{recommendation.details.peer_count} employees</dd>
                            </div>
                          )}
                          {recommendation.details.peer_average && (
                            <div>
                              <dt className="text-muted-foreground">Peer Avg Hours</dt>
                              <dd className="font-medium">{recommendation.details.peer_average}h</dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    </div>
                  ) : (
                    /* Standard Details layout for other recommendation types */
                    <div className="bg-slate-50 rounded-lg p-4">
                      <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        {recommendation.details.from_employee && (
                          <>
                            <div>
                              <dt className="text-muted-foreground">From Employee</dt>
                              <dd className="font-medium">
                                {recommendation.details.from_employee_id ? (
                                  <button onClick={() => onViewEmployee(recommendation.details.from_employee_id)} className="text-blue-600 hover:underline cursor-pointer">{recommendation.details.from_employee}</button>
                                ) : recommendation.details.from_employee}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-muted-foreground">Current OT</dt>
                              <dd className="font-medium">{recommendation.details.from_employee_ot}h</dd>
                            </div>
                          </>
                        )}
                        {recommendation.details.to_employee && (
                          <>
                            <div>
                              <dt className="text-muted-foreground">To Employee</dt>
                              <dd className="font-medium">
                                {recommendation.details.to_employee_id ? (
                                  <button onClick={() => onViewEmployee(recommendation.details.to_employee_id)} className="text-blue-600 hover:underline cursor-pointer">{recommendation.details.to_employee}</button>
                                ) : recommendation.details.to_employee}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-muted-foreground">Current OT</dt>
                              <dd className="font-medium">{recommendation.details.to_employee_ot}h</dd>
                            </div>
                          </>
                        )}
                        {recommendation.details.hours_to_shift && (
                          <div>
                            <dt className="text-muted-foreground">Hours to Shift</dt>
                            <dd className="font-medium">{recommendation.details.hours_to_shift}h</dd>
                          </div>
                        )}
                        {recommendation.details.job_title && (
                          <div>
                            <dt className="text-muted-foreground">Job Title</dt>
                            <dd className="font-medium">{recommendation.details.job_title}</dd>
                          </div>
                        )}
                        {recommendation.details.location && (
                          <div>
                            <dt className="text-muted-foreground">Location</dt>
                            <dd className="font-medium">{recommendation.details.location}</dd>
                          </div>
                        )}
                        {recommendation.details.recommended_hires && (
                          <div>
                            <dt className="text-muted-foreground">Recommended Hires</dt>
                            <dd className="font-medium">{recommendation.details.recommended_hires}</dd>
                          </div>
                        )}
                        {recommendation.details.float_workers_needed && (
                          <div>
                            <dt className="text-muted-foreground">Float Workers Needed</dt>
                            <dd className="font-medium">{recommendation.details.float_workers_needed}</dd>
                          </div>
                        )}
                        {recommendation.details.annual_ot_hours && (
                          <div>
                            <dt className="text-muted-foreground">Annual OT Hours</dt>
                            <dd className="font-medium">{recommendation.details.annual_ot_hours.toLocaleString()}h</dd>
                          </div>
                        )}
                        {recommendation.details.employees_with_ot && (
                          <div>
                            <dt className="text-muted-foreground">Employees with OT</dt>
                            <dd className="font-medium">{recommendation.details.employees_with_ot}</dd>
                          </div>
                        )}
                        {recommendation.details.avg_hourly_rate && (
                          <div>
                            <dt className="text-muted-foreground">Avg Peer Hourly Rate</dt>
                            <dd className="font-medium">${recommendation.details.avg_hourly_rate.toFixed(2)}/hr</dd>
                          </div>
                        )}
                        {recommendation.details.base_hourly_rate && (
                          <div>
                            <dt className="text-muted-foreground">Base Hourly Rate</dt>
                            <dd className="font-medium">${recommendation.details.base_hourly_rate.toFixed(2)}/hr</dd>
                          </div>
                        )}
                        {recommendation.details.float_pool_hourly_rate && (
                          <div>
                            <dt className="text-muted-foreground">Float Pool Rate</dt>
                            <dd className="font-medium text-teal-600">${recommendation.details.float_pool_hourly_rate.toFixed(2)}/hr</dd>
                          </div>
                        )}
                        {recommendation.details.overtime_hourly_rate && (
                          <div>
                            <dt className="text-muted-foreground">Overtime Rate</dt>
                            <dd className="font-medium text-amber-600">${recommendation.details.overtime_hourly_rate.toFixed(2)}/hr</dd>
                          </div>
                        )}
                        {recommendation.details.savings_per_hour && (
                          <div>
                            <dt className="text-muted-foreground">Savings Per Hour</dt>
                            <dd className="font-medium text-green-600">${recommendation.details.savings_per_hour.toFixed(2)}/hr</dd>
                          </div>
                        )}
                        {recommendation.details.estimated_annual_salary && (
                          <div>
                            <dt className="text-muted-foreground">Est. Annual Salary</dt>
                            <dd className="font-medium">${recommendation.details.estimated_annual_salary.toLocaleString()}</dd>
                          </div>
                        )}
                        {recommendation.details.pattern_reason && (
                          <div className="col-span-full">
                            <dt className="text-muted-foreground">Why This Pattern</dt>
                            <dd className="font-medium text-sm">{recommendation.details.pattern_reason}</dd>
                          </div>
                        )}
                        {recommendation.details.pattern_fit && (
                          <div className="col-span-full">
                            <dt className="text-muted-foreground">Why Float Pool Fits</dt>
                            <dd className="font-medium text-sm text-teal-600">{recommendation.details.pattern_fit}</dd>
                          </div>
                        )}
                        {recommendation.details.cost_calculation_note && (
                          <div className="col-span-full">
                            <dt className="text-muted-foreground">Cost Calculation</dt>
                            <dd className="font-medium text-xs text-blue-600">{recommendation.details.cost_calculation_note}</dd>
                          </div>
                        )}
                        {recommendation.details.supervisor_name && (
                          <div>
                            <dt className="text-muted-foreground">Supervisor</dt>
                            <dd className="font-medium">{recommendation.details.supervisor_name}</dd>
                          </div>
                        )}
                        {recommendation.details.team_size && (
                          <div>
                            <dt className="text-muted-foreground">Team Size</dt>
                            <dd className="font-medium">{recommendation.details.team_size} employees</dd>
                          </div>
                        )}
                        {recommendation.details.variance_percentage && (
                          <div>
                            <dt className="text-muted-foreground">Variance from Avg</dt>
                            <dd className="font-medium text-amber-600">+{recommendation.details.variance_percentage}%</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  )}
                </div>
              )}

              {/* Float Pool Economics - Special section for Float Pool recommendations */}
              {recommendation.float_pool_economics && (
                <div>
                  <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                    <UsersRound size={18} className="text-teal-500" />
                    Float Pool Economics
                  </h4>
                  <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
                    {/* Rate Comparison */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-xs text-muted-foreground mb-1">Base Rate</div>
                        <div className="text-lg font-bold text-slate-700">${recommendation.float_pool_economics.regular_base_rate}/hr</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border-2 border-amber-200">
                        <div className="text-xs text-amber-600 mb-1">Overtime (1.5x)</div>
                        <div className="text-lg font-bold text-amber-600">${recommendation.float_pool_economics.overtime_rate}/hr</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center border-2 border-teal-300">
                        <div className="text-xs text-teal-600 mb-1">Float Pool</div>
                        <div className="text-lg font-bold text-teal-600">${recommendation.float_pool_economics.float_pool_rate}/hr</div>
                      </div>
                    </div>
                    
                    {/* Comparison Note */}
                    <div className="bg-white rounded-lg p-3 mb-4">
                      <p className="text-sm font-medium text-teal-700">{recommendation.float_pool_economics.rate_comparison}</p>
                      <p className="text-xs text-muted-foreground mt-1">{recommendation.float_pool_economics.benefits_note}</p>
                    </div>
                    
                    {/* When to Use */}
                    <div>
                      <div className="text-xs font-medium text-teal-700 mb-2 uppercase tracking-wide">Best for situations where:</div>
                      <ul className="space-y-1">
                        {recommendation.float_pool_economics.when_to_use?.map((item, idx) => (
                          <li key={`when-${idx}-${item.slice(0, 10)}`} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 size={14} className="text-teal-500 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-600">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Risks */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Risks of Implementing */}
                <div>
                  <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-500" />
                    Risks of Implementing
                  </h4>
                  <ul className="space-y-2">
                    {recommendation.risks.implementing.map((risk, idx) => (
                      <li key={`risk-impl-${idx}-${risk.slice(0, 10)}`} className="flex items-start gap-2 text-sm">
                        <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Risks of NOT Implementing */}
                <div>
                  <h4 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
                    <Shield size={18} className="text-red-500" />
                    Risks of NOT Implementing
                  </h4>
                  <ul className="space-y-2">
                    {recommendation.risks.not_implementing.map((risk, idx) => (
                      <li key={`risk-not-${idx}-${risk.slice(0, 10)}`} className="flex items-start gap-2 text-sm">
                        <AlertCircle size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{risk}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const Recommendations = () => {
  const { API } = React.useContext(AppContext);
  const { filters } = useFilters();
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);

  // DB-freshness-aware cached fetch
  const fetchRecs = useCallback(async () => {
    const response = await axios.post(`${API}/recommendations`, {
      start_date: filters.start_date,
      end_date: filters.end_date,
      location: filters.location,
      department: filters.department,
      pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null
    });
    return response.data;
  }, [API, filters.start_date, filters.end_date, filters.location, filters.department, filters.pay_types]);

  const { data: cachedData, loading: cacheLoading } = useCachedData(
    'recommendations',
    fetchRecs,
    [filters.start_date, filters.end_date, filters.location, filters.department, JSON.stringify(filters.pay_types)]
  );

  useEffect(() => {
    if (cachedData) setRecommendations(cachedData);
  }, [cachedData]);

  const loading = cacheLoading || manualLoading;

  // Manual refresh (force re-fetch, bypasses cache)
  const generateRecommendations = useCallback(async (forceRefresh = false) => {
    if (!forceRefresh) return;
    setManualLoading(true);
    try {
      const response = await axios.post(`${API}/recommendations`, {
        start_date: filters.start_date,
        end_date: filters.end_date,
        location: filters.location,
        department: filters.department,
        pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null
      });
      setRecommendations(response.data);
      // Invalidate sessionStorage cache so next visit re-fetches.
      // Storage operations can fail in Safari private mode — non-fatal.
      const cacheKey = 'recommendations_' + [filters.start_date, filters.end_date, filters.location, filters.department, JSON.stringify(filters.pay_types)].map(d => d ?? '').join('_');
      try {
        sessionStorage.removeItem(cacheKey);
        sessionStorage.removeItem(cacheKey + '__meta');
      } catch (storageErr) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('sessionStorage cleanup after rec generation failed:', storageErr);
        }
      }
      toast.success(`Generated ${response.data.recommendations.length} recommendations`);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Generate recommendations failed:', error);
      }
      toast.error('Failed to generate recommendations');
    } finally {
      setManualLoading(false);
    }
  }, [API, filters.start_date, filters.end_date, filters.location, filters.department, filters.pay_types]);

  // Calculate hiring summary
  const hiringSummary = useMemo(() => {
    if (!recommendations?.recommendations) return null;
    
    const hiringRecs = recommendations.recommendations.filter(r => r.type === 'HIRE_STAFF');
    if (hiringRecs.length === 0) return null;

    const totalHires = hiringRecs.reduce((sum, r) => sum + (r.details?.recommended_hires || 0), 0);
    const totalHiringCost = hiringRecs.reduce((sum, r) => sum + (r.financial_impact?.new_hire_cost || 0), 0);
    const totalOTSavings = hiringRecs.reduce((sum, r) => sum + (r.financial_impact?.current_annual_ot_cost || 0), 0);
    const netSavings = hiringRecs.reduce((sum, r) => sum + (r.financial_impact?.annual_savings || 0), 0);
    const avgPaybackMonths = hiringRecs.reduce((sum, r) => {
      const payback = r.financial_impact?.payback_period;
      if (typeof payback === 'string') {
        const months = parseFloat(payback);
        return sum + (isNaN(months) ? 0 : months);
      }
      return sum;
    }, 0) / hiringRecs.length;

    return {
      count: hiringRecs.length,
      totalHires,
      totalHiringCost,
      totalOTSavings,
      netSavings,
      avgPaybackMonths: avgPaybackMonths || 0
    };
  }, [recommendations]);

  // Calculate float pool summary
  const floatPoolSummary = useMemo(() => {
    if (!recommendations?.recommendations) return null;
    
    const floatPoolRecs = recommendations.recommendations.filter(r => r.type === 'FLOAT_POOL');
    if (floatPoolRecs.length === 0) return null;

    const totalFloatWorkers = floatPoolRecs.reduce((sum, r) => sum + (r.details?.float_workers_needed || 0), 0);
    const totalCurrentOTCost = floatPoolRecs.reduce((sum, r) => sum + (r.financial_impact?.current_annual_ot_cost || 0), 0);
    const totalFloatPoolCost = floatPoolRecs.reduce((sum, r) => sum + (r.financial_impact?.float_pool_cost || 0), 0);
    const netSavings = floatPoolRecs.reduce((sum, r) => sum + (r.financial_impact?.annual_savings || 0), 0);
    const avgSavingsPercentage = floatPoolRecs.reduce((sum, r) => sum + (r.financial_impact?.savings_percentage || 0), 0) / floatPoolRecs.length;

    return {
      count: floatPoolRecs.length,
      totalFloatWorkers,
      totalCurrentOTCost,
      totalFloatPoolCost,
      netSavings,
      avgSavingsPercentage: avgSavingsPercentage || 0
    };
  }, [recommendations]);

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toFixed(0)}`;
  };

  return (
    <div className="p-6 md:p-8" data-testid="recommendations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-1 flex items-center gap-3">
            <Lightbulb className="text-amber-500" />
            Recommendations
          </h1>
          <p className="text-muted-foreground">AI-powered insights to reduce overtime costs</p>
        </div>
        <Button
          onClick={() => generateRecommendations(true)}
          disabled={loading}
          className="bg-[#2E5C8A] hover:bg-[#1e3a5f]"
          data-testid="generate-recommendations-btn"
        >
          {loading ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Refresh Recommendations
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards - Show skeleton on initial load */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {[...Array(5)].map((_, i) => (
            <div key={`rec-skel-${i}`} className="bg-white rounded-xl border border-border p-4 shadow-sm">
              <Skeleton className="h-4 w-24 mb-3" />
              <Skeleton className="h-8 w-16" />
            </div>
          ))}
        </div>
      ) : recommendations && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-border p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <Lightbulb size={16} />
              Total Recommendations
            </div>
            <div className="text-2xl font-bold text-[#1e3a5f]">
              {recommendations.summary.total_recommendations}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-xl border border-border p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <DollarSign size={16} className="text-green-600" />
              Potential Savings
            </div>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(recommendations.summary.total_potential_savings)}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-border p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <AlertCircle size={16} className="text-red-500" />
              High Priority
            </div>
            <div className="text-2xl font-bold text-red-600">
              {recommendations.summary.high_priority_count}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl border border-border p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <AlertTriangle size={16} className="text-amber-500" />
              Medium Priority
            </div>
            <div className="text-2xl font-bold text-amber-600">
              {recommendations.summary.medium_priority_count}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-border p-4 shadow-sm"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
              <CheckCircle2 size={16} className="text-green-500" />
              Low Priority
            </div>
            <div className="text-2xl font-bold text-green-600">
              {recommendations.summary.low_priority_count}
            </div>
          </motion.div>
        </div>
      )}

      {/* Hiring Recommendations Summary Panel */}
      {hiringSummary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200 p-5 mb-6"
          data-testid="hiring-summary-panel"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <Briefcase className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-green-800 text-lg">Hiring Recommendations Summary</h3>
              <p className="text-sm text-green-600">{hiringSummary.count} opportunities identified</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium mb-1">Total Hires Needed</div>
              <div className="text-xl font-bold text-green-800">{hiringSummary.totalHires}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium mb-1">Total Hiring Cost</div>
              <div className="text-xl font-bold text-green-800">{formatCurrency(hiringSummary.totalHiringCost)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium mb-1">Current OT Cost</div>
              <div className="text-xl font-bold text-amber-600">{formatCurrency(hiringSummary.totalOTSavings)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium mb-1">Net Annual Savings</div>
              <div className="text-xl font-bold text-green-700">{formatCurrency(hiringSummary.netSavings)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-green-600 font-medium mb-1">Avg Payback Period</div>
              <div className="text-xl font-bold text-green-800">{hiringSummary.avgPaybackMonths.toFixed(1)} mo</div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Float Pool Recommendations Summary Panel */}
      {floatPoolSummary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-200 p-5 mb-6"
          data-testid="float-pool-summary-panel"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
              <UsersRound className="text-white" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-teal-800 text-lg">Float Pool Recommendations</h3>
              <p className="text-sm text-teal-600">{floatPoolSummary.count} locations could benefit from flexible staffing</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-teal-600 font-medium mb-1">Float Workers Needed</div>
              <div className="text-xl font-bold text-teal-800">{floatPoolSummary.totalFloatWorkers}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-teal-600 font-medium mb-1">Current OT Cost</div>
              <div className="text-xl font-bold text-amber-600">{formatCurrency(floatPoolSummary.totalCurrentOTCost)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-teal-600 font-medium mb-1">Float Pool Cost</div>
              <div className="text-xl font-bold text-teal-800">{formatCurrency(floatPoolSummary.totalFloatPoolCost)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-teal-600 font-medium mb-1">Net Annual Savings</div>
              <div className="text-xl font-bold text-green-700">{formatCurrency(floatPoolSummary.netSavings)}</div>
            </div>
            <div className="bg-white/70 rounded-lg p-3">
              <div className="text-xs text-teal-600 font-medium mb-1">Avg Savings %</div>
              <div className="text-xl font-bold text-teal-800">{floatPoolSummary.avgSavingsPercentage.toFixed(0)}%</div>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-white/50 rounded-lg">
            <p className="text-xs text-teal-700">
              <strong>Why Float Pool?</strong> Float pool workers earn a 10-20% premium over base pay but cost 20-33% less than overtime. 
              Plus, they typically don't receive full benefits, adding 30-40% in additional savings not shown here.
            </p>
          </div>
        </motion.div>
      )}

      {/* Recommendations List */}
      {!recommendations ? (
        <div className="bg-white rounded-xl border border-border p-12 text-center">
          <Lightbulb className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">No Recommendations Yet</h3>
          <p className="text-muted-foreground mb-6">
            Click "Generate Recommendations" to analyze your overtime data and get actionable insights.
          </p>
          <Button
            onClick={generateRecommendations}
            disabled={loading}
            className="bg-[#2E5C8A] hover:bg-[#1e3a5f]"
          >
            {loading ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Lightbulb className="mr-2 h-4 w-4" />
                Generate Recommendations
              </>
            )}
          </Button>
        </div>
      ) : recommendations.recommendations.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-12 text-center">
          <CheckCircle2 className="h-16 w-16 mx-auto text-green-500/50 mb-4" />
          <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">Looking Good!</h3>
          <p className="text-muted-foreground">
            No significant optimization opportunities found with current filters. Try expanding your date range or removing location/department filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="recommendations-list">
          {recommendations.recommendations.map((rec, idx) => (
            <RecommendationCard key={rec.id} recommendation={rec} index={idx} onViewEmployee={(empId) => navigate(`/employee/${empId}`)} />
          ))}
        </div>
      )}

      {/* Footer */}
      {recommendations && recommendations.generated_at && (
        <div className="mt-6 text-center text-sm text-muted-foreground">
          Generated at {new Date(recommendations.generated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default Recommendations;
